import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import SidebarContent from "./SidebarContent";
import { useNavigationContext } from "../../context/NavigationContext";

const useLockBodyScroll = (locked: boolean) => {
  useEffect(() => {
    if (!locked) return;
    const prev = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prev;
    };
  }, [locked]);
};

const BodyPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted ? createPortal(children, document.body) : null;
};

type MobileDrawerProps = {
  open: boolean;
  onClose: () => void;
};

const MobileDrawer: React.FC<MobileDrawerProps> = ({ open, onClose }) => (
  <BodyPortal>
    {open ? (
      <div
        onClick={onClose}
        className="fixed inset-0 z-[2147483647] bg-black/40 backdrop-blur-[2px] transition-opacity xl:hidden"
        aria-hidden="true"
      />
    ) : null}

    <aside
      role="dialog"
      aria-modal={open ? "true" : "false"}
      aria-label="Sidebar Navigation"
      className={[
        "fixed inset-y-0 left-0 xl:hidden",
        "z-[2147483647] isolate",
        "flex h-screen w-[300px] flex-col border-r border-gray-200 bg-white shadow-lg",
        "transition-transform duration-300 ease-out",
        open ? "translate-x-0" : "-translate-x-full",
      ].join(" ")}
    >
      <SidebarContent onClose={onClose} />
    </aside>
  </BodyPortal>
);

const DesktopSidebar: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <aside
    aria-label="Sidebar Navigation"
    className="hidden flex-col border-r border-gray-200 bg-white xl:static xl:inset-auto xl:flex xl:h-screen xl:w-[340px] isolate"
  >
    <SidebarContent onClose={onClose} />
  </aside>
);

const SideBar: React.FC = () => {
  const { showSideBar, setShowSideBar } = useNavigationContext();
  const close = () => setShowSideBar(false);

  useLockBodyScroll(showSideBar);

  return (
    <>
      <DesktopSidebar onClose={close} />
      <MobileDrawer open={showSideBar} onClose={close} />

      <style>{`
        .sidebar-scroll { scrollbar-width: thin; scrollbar-color: rgba(100,116,139,0.35) transparent; }
        .sidebar-scroll::-webkit-scrollbar { width: 2px; }
        .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .sidebar-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(100,116,139,0.35);
          border-radius: 9999px;
          border: 2px solid transparent;
          background-clip: padding-box;
        }
        .sidebar-scroll:hover::-webkit-scrollbar-thumb { background-color: rgba(100,116,139,0.5); }
      `}</style>
    </>
  );
};

export default SideBar;
